import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { ArrowLeft, RefreshCw, MapPin, Navigation, Calendar, Clock, Truck } from "lucide-react";

function VehicleTrackingDetails() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (vehicleId) {
      fetchVehicleDetails();
      fetchTrackingHistory();
    }
  }, [vehicleId]);

  const fetchVehicleDetails = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.busTrackingVehicle(vehicleId));
      setVehicleDetails(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch vehicle details");
    }
  };

  const fetchTrackingHistory = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.busTrackingHistory(`vehicleId=${vehicleId}&pageSize=1000`));
      setTrackingHistory(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch tracking history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDuration = (durationString) => {
    if (!durationString || durationString === "N/A") return "N/A";
    return durationString;
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  const getVehicleTypeBadge = (type) => {
    const colors = type === "bus" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800";
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>
        {type ? type.toUpperCase() : "N/A"}
      </span>
    );
  };

  const openGoogleMaps = (latitude, longitude) => {
    if (latitude && longitude) {
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
    }
  };

  // Group tracking history by date
  const groupByDate = (data) => {
    const grouped = {};
    data.forEach((item) => {
      if (item.locationSharingStartTime) {
        const date = new Date(item.locationSharingStartTime);
        const dateKey = date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(item);
      }
    });
    // Sort dates in descending order (newest first)
    return Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((acc, key) => {
        acc[key] = grouped[key].sort((a, b) => 
          new Date(b.locationSharingStartTime) - new Date(a.locationSharingStartTime)
        );
        return acc;
      }, {});
  };

  const groupedData = groupByDate(trackingHistory);

  if (loading && !vehicleDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/dashboard/bus-tracking")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">Vehicle Tracking Details</h2>
        </div>
        <button
          onClick={() => {
            fetchVehicleDetails();
            fetchTrackingHistory();
          }}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          {error}
        </div>
      )}

      {vehicleDetails && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">Vehicle Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">Vehicle Number:</span>
              <p className="text-lg font-semibold">{vehicleDetails.vehicleNumber}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Type:</span>
              <p className="text-lg">{getVehicleTypeBadge(vehicleDetails.type)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">School:</span>
              <p className="text-lg">{vehicleDetails.school?.name || "N/A"}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <p className="text-lg">{getStatusBadge(vehicleDetails.locationSharingActive)}</p>
            </div>
            {vehicleDetails.driver && (
              <div>
                <span className="text-sm font-medium text-gray-600">Driver:</span>
                <p className="text-lg">{vehicleDetails.driver.name} ({vehicleDetails.driver.phoneNumber})</p>
              </div>
            )}
            {vehicleDetails.conductor && (
              <div>
                <span className="text-sm font-medium text-gray-600">Conductor:</span>
                <p className="text-lg">{vehicleDetails.conductor.name} ({vehicleDetails.conductor.phoneNumber})</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Tracking History (Day-wise)
          </h3>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-gray-600">Loading tracking history...</p>
            </div>
          ) : Object.keys(groupedData).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(groupedData).map(([date, items]) => (
                <div key={date} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-800">{date}</h4>
                    <span className="ml-2 text-sm text-gray-500">({items.length} session{items.length !== 1 ? 's' : ''})</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                                {formatDate(item.locationSharingStartTime)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(item.locationSharingEndTime)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {formatDuration(item.duration)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {getStatusBadge(item.isCurrentlyActive !== undefined ? item.isCurrentlyActive : item.isActive)}
                            </td>
                                     <td className="px-4 py-3 whitespace-nowrap text-sm">
                                       {item.currentLocation && item.currentLocation.latitude && item.currentLocation.longitude ? (
                                         <button
                                           onClick={() => openGoogleMaps(item.currentLocation.latitude, item.currentLocation.longitude)}
                                           className="text-blue-600 hover:text-blue-800 flex items-center"
                                         >
                                           <MapPin className="w-4 h-4 mr-1" />
                                           View Map
                                         </button>
                                       ) : item.lastKnownLocation && item.lastKnownLocation.latitude && item.lastKnownLocation.longitude ? (
                                         <button
                                           onClick={() => openGoogleMaps(item.lastKnownLocation.latitude, item.lastKnownLocation.longitude)}
                                           className="text-blue-600 hover:text-blue-800 flex items-center"
                                         >
                                           <MapPin className="w-4 h-4 mr-1" />
                                           View Map
                                         </button>
                                       ) : (
                                         <span className="text-gray-400">No location</span>
                                       )}
                                     </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Truck className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No tracking history found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VehicleTrackingDetails;
